import { Fragment, useContext, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
  useLocation,
} from "react-router-dom";
import "./App.css";
import Navbar from "./Layouts/Header";
import Footer from "./Layouts/Footer";
import { Dashboard } from "./Pages/Dashboard";
import { AboutUs } from "./Pages/AboutUs";
import {
  Awards,
  ElevateStandardServices,
  GrowWithFWPI,
  OurServices,
} from "./Pages/ServicePage";
import { CreateGymProfile, GymProfile, Gyms } from "./Pages/GymFacilities";
import {
  CreateTrainer,
  TrainerProfile,
  Trainers,
} from "./Pages/TrainerDetails";
import { AuthContext, AuthProvider } from "./Contexts/AuthContext";
import ScrollToTop from "./Layouts/ScrollToTop";
import FWPI404 from "./Pages/404";
import { MyProfile } from "./Pages/MyProfile";
import {
  CreateTrainingProvider,
  TrainingProviders,
} from "./Pages/TrainingProvides";
import { LoadingPage } from "./Pages/LoadingPage/LoadingPage";
import { ContactUs } from "./Pages/Contactus";
import { MembershipPlans } from "./Pages/MembershipPlans";
import _ from "lodash";
import WhatsAppButton from "./Components/WhatsappButton";
import LeadGenerationPopup from "./Components/LeadGenerationPopup";
import { PrivacyPolicy, TermsAndConditions } from "./Pages/Policy";

function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <AllRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

const ProtectedRoute = ({
  allowedRoles = ["trainer", "gym"],
  redirectTo = "/",
}) => {
  const { token, user, isLoading, setIsLoading, setIsLoginModal } =
    useContext(AuthContext);
  const location = useLocation();

  // ✓ Always call hooks at the top level (no conditional)
  useEffect(() => {
    if (!token && !isLoading) {
      setIsLoginModal(true);
      setIsLoading(false);
    }
  }, [token, isLoading, setIsLoginModal, setIsLoading]);

  // Still loading auth state → show loader
  if (isLoading) return <LoadingPage />;

  // Not authenticated → Login modal opened by effect, keep showing loader/blank UI
  if (!token) return <LoadingPage />;

  if (location.pathname === "/my-profile") {
    return <Outlet />;
  }
  // Authenticated but role not allowed → redirect
  if (!allowedRoles.includes(user?.role)) {
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  }

  // Auth OK → allow route
  return <Outlet />;
};

const AllRoutes = () => {
  const { hash } = useLocation();

  const { membership } = useContext(AuthContext);

  useEffect(() => {
    if (!hash) return; // e.g. #about
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (!el) return;

    const navbarHeight = 100; // ⬅️ change this to your nav height (in px)

    const elementTop = el.getBoundingClientRect().top + window.scrollY;
    const scrollTo = elementTop - navbarHeight;

    window.scrollTo({
      top: scrollTo,
      behavior: "smooth",
    });
  }, [hash]);

  return (
    <div className="pt-[10px]">
      <Routes>
        <Route element={<Layouts />}>
          <Route path="/" element={<Dashboard />} />
          <Route path="/about" element={<AboutUs />} />
          <Route
            element={
              <ProtectedRoute
                allowedRoles={["trainer", "gym", "training-provider"]}
              />
            }
          >
            <Route path="/my-profile" element={<MyProfile />} />
          </Route>
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />

          {(!membership?.status || membership?.status === "pending") && (
            <Route
              path="/membership/:planType"
              element={<MembershipPlans key={_.uniqueId("fwpi_membership")} />}
            />
          )}

          <Route path="/services">
            {/* nested routes */}
            <Route index element={<Gyms />} />
            <Route path="our-services" element={<OurServices />} />
            <Route
              path="elevate-standards"
              element={<ElevateStandardServices />}
            />
            <Route path="grow-with-fwpi" element={<GrowWithFWPI />} />
            <Route path="awards" element={<Awards />} />
          </Route>

          <Route path="/gym">
            {/* nested routes */}
            <Route index element={<Gyms />} />
            <Route path="profile/:gymId" element={<GymProfile />} />
            <Route element={<ProtectedRoute allowedRoles={["user"]} />}>
              <Route path="create" element={<CreateGymProfile />} />
            </Route>
          </Route>

          <Route path="/trainer">
            {/* nested routes */}
            <Route index element={<Trainers />} />
            <Route path="profile/:trainerId" element={<TrainerProfile />} />
            <Route element={<ProtectedRoute allowedRoles={["user"]} />}>
              <Route path="create" element={<CreateTrainer />} />
            </Route>
          </Route>

          <Route path="/training-provider">
            {/* nested routes */}
            <Route index element={<TrainingProviders />} />
            <Route element={<ProtectedRoute allowedRoles={["user"]} />}>
              <Route path="create" element={<CreateTrainingProvider />} />
            </Route>
          </Route>

          <Route path="*" element={<FWPI404 />} />
        </Route>
      </Routes>
    </div>
  );
};

const Layouts = () => {
  const location = useLocation();
  const isHome = location.pathname === "/";

  return (
    <div className="flex flex-col justify-between min-h-screen">
      <ScrollToTop />
      <Navbar />

      {/* Spacer only for Home page */}
      {isHome && <div id="header-spacer" className="fwpi-home-spacer" />}

      <main className="min-h-[calc(100vh-48vh)] flex flex-col h-full flex-auto">
        <Outlet />
      </main>

      <WhatsAppButton />
      {/* Lead popup only on homepage */}
      {isHome && <LeadGenerationPopup />}
      <Footer />
    </div>
  );
};
export default App;
